import { decodeEventLog, encodeFunctionData, parseAbi, getAddress, parseEther, parseUnits } from 'viem';
import { getEvmProvider, publicClient, ensureEvmChain, waitForTransactionReceipt } from './evmNetwork';
import {
  UNISWAP_V3_FACTORY,
  INCENTIFI_SWAP_ROUTER,
  WETH_ADDRESS,
  POOL_FEE,
  PROTOCOL_FEE_BPS,
  CREATOR_FEE_BPS,
  LOSS_REWARD_FEE_BPS,
} from './uniswapAddresses';
import {
  fetchBondingCurveState,
  calculateTokensOut,
  calculateEthOut,
  executeBondingCurveBuy,
  executeBondingCurveSell,
  TOTAL_TOKEN_SUPPLY,
  REFERENCE_ETH_USD,
  BondingCurveState,
} from './bondingCurve';
import {
  isV4LaunchedToken,
  fetchV4CurveState,
  executeV4Buy,
  executeV4Sell,
} from './bondingCurveV4';
import {
  fetchLegiblePoolState,
  quoteLegibleBuy,
  quoteLegibleSell,
  executeLegibleBuy,
  executeLegibleSell,
} from './legiblePool';
import { resolveTokenVenue, type TokenVenue } from './tokenVenue';

const FACTORY_ABI = parseAbi([
  'function getPool(address tokenA, address tokenB, uint24 fee) view returns (address pool)',
]);

const POOL_ABI = parseAbi([
  'function slot0() view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)',
]);

const ERC20_ABI = parseAbi([
  'function approve(address spender, uint256 amount) returns (bool)',
  'function balanceOf(address account) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
]);

const INCENTIFI_ROUTER_ABI = parseAbi([
  'function buyToken(address token, uint256 amountOutMinimum, uint256 deadline) payable returns (uint256 amountOut)',
  'function sellToken(address token, uint256 amountIn, uint256 amountOutMinimum, uint256 deadline) returns (uint256 amountOut)',
  'event Bought(address indexed buyer, address indexed token, uint256 ethIn, uint256 tokensOut, uint256 creatorFee, uint256 lossRewardFee)',
  'event Sold(address indexed seller, address indexed token, uint256 tokensIn, uint256 ethOut, uint256 creatorFee, uint256 lossRewardFee)',
]);

const toQuantityHex = (value: bigint) => `0x${value.toString(16)}`;

const waitForReceipt = waitForTransactionReceipt;

const priceInEth = (sqrtPriceX96: bigint, isTokenFirst: boolean) => {
  const Q96 = 1n << 96n;
  const sqrtPrice = Number(sqrtPriceX96) / Number(Q96);
  const rawPrice = sqrtPrice * sqrtPrice;
  return isTokenFirst ? 1 / rawPrice : rawPrice;
};

export type ConfirmedPoolSwap = {
  side: 'buy' | 'sell';
  amountToken: number;
  amountEth: number;
  priceEth: number;
};

export const getPoolQuote = async (tokenAddress: string) => {
  const token = getAddress(tokenAddress);
  const weth = getAddress(WETH_ADDRESS);

  const poolAddress = await publicClient.readContract({
    address: getAddress(UNISWAP_V3_FACTORY),
    abi: FACTORY_ABI,
    functionName: 'getPool',
    args: [token, weth, POOL_FEE],
  } as any);

  if (!poolAddress || poolAddress === '0x0000000000000000000000000000000000000000') {
    throw new Error('No liquidity pool found for this token yet.');
  }

  const slot0 = await publicClient.readContract({
    address: poolAddress as `0x${string}`,
    abi: POOL_ABI,
    functionName: 'slot0',
  } as any);
  const sqrtPriceX96 = BigInt(slot0[0]);

  if (sqrtPriceX96 <= 0n) {
    throw new Error('Pool exists but has no price yet.');
  }

  const isTokenFirst = BigInt(token) < BigInt(weth);

  return { poolAddress: poolAddress as `0x${string}`, sqrtPriceX96, isTokenFirst };
};

export type UnifiedMarketState = {
  isBondingCurve: boolean;
  isGraduated: boolean;
  poolAddress?: string;
  curveAddress?: string;
  priceEth: number;
  priceUsd: number;
  marketCapUsd: number;
  progressBps: number;
  circulatingTokens: number;
  realEthReserveEth: number;
  realTokenReserveTokens: number;
  /** True for a token launched through either V4 factory (see ./bondingCurveV4, ./legiblePool). */
  isV4?: boolean;
  /**
   * Which contracts this token trades through — resolved per token from the DB tag / the chain
   * (see ./tokenVenue). 'legible' tokens are real V4 pools traded via UniversalRouter before and
   * after graduation; 'v4-generic' tokens use IncentifiV4Router (pre-graduation only); 'v3' the
   * per-token curve + IncentifiSwapRouter.
   */
  venue?: TokenVenue;
  /** Legible pools only: the hook (explorer target) and the V4 poolId. */
  hookAddress?: string;
  poolId?: string;
  /**
   * False only for a graduated V4 pool — IncentifiV4Router doesn't support
   * post-graduation trades and no replacement router is deployed yet (see
   * bondingCurveV4.ts's header comment). Always true otherwise.
   */
  tradingSupported?: boolean;
};

/**
 * Unified query resolving, in order: V4 bonding curve / V4 pool, v3 Bonding
 * Curve, or post-graduation Uniswap V3 — whichever actually applies to this
 * token. New launches go through V4 (see src/lib/createEvmToken.ts); tokens
 * launched before that switch remain on v3, so both paths stay live here.
 */
export const getUnifiedMarketState = async (
  tokenAddress: string,
  ethPriceUsd: number = REFERENCE_ETH_USD
): Promise<UnifiedMarketState> => {
  const venue = await resolveTokenVenue(tokenAddress);

  // Legible pool (PR #17): price from slot0, progress/reserves from the hook's legacy view.
  if (venue === 'legible') {
    const st = await fetchLegiblePoolState(tokenAddress, ethPriceUsd);
    return {
      isBondingCurve: !st.graduated,
      isGraduated: st.graduated,
      isV4: true,
      venue: 'legible',
      tradingSupported: true,
      hookAddress: st.hookAddress,
      poolId: st.poolId,
      poolAddress: st.uniswapPoolAddress || undefined,
      curveAddress: undefined,
      priceEth: st.currentPriceEth,
      priceUsd: st.currentPriceEth * ethPriceUsd,
      marketCapUsd: st.marketCapUsd,
      progressBps: st.progressBps,
      circulatingTokens: st.circulatingTokens,
      realEthReserveEth: Number(st.realEthReserve) / 1e18,
      realTokenReserveTokens: Number(st.realTokenReserve) / 1e18,
    };
  }

  // Previous V4 trio (GenericSell hook + IncentifiV4Router) — unchanged path.
  if (venue === 'v4-generic' || (venue === 'unknown' && (await isV4LaunchedToken(tokenAddress)))) {
    const v4State = await fetchV4CurveState(tokenAddress, ethPriceUsd);
    return {
      isBondingCurve: !v4State.graduated,
      isGraduated: v4State.graduated,
      isV4: true,
      venue: 'v4-generic',
      tradingSupported: v4State.tradingSupported,
      poolAddress: v4State.uniswapPoolAddress || undefined,
      curveAddress: undefined, // V4 has no per-token curve contract
      priceEth: v4State.currentPriceEth,
      priceUsd: v4State.currentPriceEth * ethPriceUsd,
      marketCapUsd: v4State.marketCapUsd,
      progressBps: v4State.progressBps,
      circulatingTokens: v4State.circulatingTokens,
      realEthReserveEth: Number(v4State.realEthReserve) / 1e18,
      realTokenReserveTokens: Number(v4State.realTokenReserve) / 1e18,
    };
  }

  const curveState = await fetchBondingCurveState(tokenAddress, ethPriceUsd);

  if (!curveState.graduated) {
    return {
      isBondingCurve: true,
      isGraduated: false,
      curveAddress: curveState.curveAddress || undefined,
      priceEth: curveState.currentPriceEth,
      priceUsd: curveState.currentPriceEth * ethPriceUsd,
      marketCapUsd: curveState.marketCapUsd,
      progressBps: curveState.progressBps,
      circulatingTokens: curveState.circulatingTokens,
      realEthReserveEth: Number(curveState.realEthReserve) / 1e18,
      realTokenReserveTokens: Number(curveState.realTokenReserve) / 1e18,
    };
  }

  // Token is graduated: query Uniswap V3 pool
  try {
    const { poolAddress, sqrtPriceX96, isTokenFirst } = await getPoolQuote(tokenAddress);
    const pEth = priceInEth(sqrtPriceX96, isTokenFirst);
    const pUsd = pEth * ethPriceUsd;

    return {
      isBondingCurve: false,
      isGraduated: true,
      poolAddress,
      priceEth: pEth,
      priceUsd: pUsd,
      marketCapUsd: 1_000_000_000 * pUsd,
      progressBps: 10000,
      circulatingTokens: 1_000_000_000,
      realEthReserveEth: 5.85386,
      realTokenReserveTokens: 212096496.0558,
    };
  } catch {
    // Fallback gracefully
    return {
      isBondingCurve: false,
      isGraduated: true,
      priceEth: curveState.currentPriceEth,
      priceUsd: curveState.currentPriceEth * ethPriceUsd,
      marketCapUsd: curveState.marketCapUsd,
      progressBps: 10000,
      circulatingTokens: 1_000_000_000,
      realEthReserveEth: 5.85386,
      realTokenReserveTokens: 212096496.0558,
    };
  }
};

const applySlippage = (amount: bigint, slippageBps: number) => {
  const bps = BigInt(Math.max(0, Math.min(10_000, Math.round(slippageBps))));
  return (amount * (10_000n - bps)) / 10_000n;
};

export type SwapResult = {
  txHash: string;
  trade: ConfirmedPoolSwap;
};

/**
 * Universal Buy Function: routes to Bonding Curve pre-graduation, or Uniswap V3 post-graduation.
 */
export const buyToken = async (
  tokenAddress: string,
  trader: string,
  ethAmount: string | number | bigint,
  slippagePct: number,
  ethPriceUsd: number = REFERENCE_ETH_USD
): Promise<SwapResult> => {
  const provider = getEvmProvider();
  if (!provider) throw new Error('Connect wallet first.');
  await ensureEvmChain();

  const token = getAddress(tokenAddress);
  const traderAddr = getAddress(trader);

  const ethWei = typeof ethAmount === 'bigint'
    ? ethAmount
    : typeof ethAmount === 'number'
      ? parseEther(ethAmount.toFixed(18))
      : parseEther(String(ethAmount).trim());

  if (ethWei <= 0n) throw new Error('Enter a valid ETH amount.');

  const venue = await resolveTokenVenue(token);

  // 0a. Legible pool: quote from the V4 Quoter, swap through UniversalRouter, pre- and
  //     post-graduation alike (there is no "graduated = no trading" state for these pools).
  if (venue === 'legible') {
    const { amount: quotedTokensOut } = await quoteLegibleBuy(token, ethWei);
    if (quotedTokensOut <= 0n) throw new Error('The pool returned a zero quote for this amount.');
    const minTokensOut = applySlippage(quotedTokensOut, slippagePct * 100);
    const res = await executeLegibleBuy(token, ethWei, minTokensOut, quotedTokensOut);
    const tokensPurchasedNum = Number(res.tokenAmount) / 1e18;
    const ethPaidNum = Number(res.ethAmount) / 1e18;
    return {
      txHash: res.txHash,
      trade: {
        side: 'buy',
        amountToken: tokensPurchasedNum,
        amountEth: ethPaidNum,
        priceEth: tokensPurchasedNum > 0 ? ethPaidNum / tokensPurchasedNum : 0,
      },
    };
  }

  // 0b. Previous V4 trio: route to the V4 factory/hook/router instead (unchanged).
  if (venue === 'v4-generic' || (venue === 'unknown' && (await isV4LaunchedToken(token)))) {
    const v4State = await fetchV4CurveState(token, ethPriceUsd);
    if (v4State.graduated) {
      // executeV4Buy would throw this same error — surfaced here first so the
      // quote math below isn't computed against a curve that no longer exists.
      throw new Error(
        "This token has graduated to a real, permissionless V4 pool. Trading it directly through this site isn't " +
        'wired up yet — post-graduation V4 trading needs its own dedicated router, which hasn\'t been deployed.'
      );
    }

    const { tokensOut } = calculateTokensOut(ethWei, v4State.realEthReserve, v4State.realTokenReserve);
    const minTokensOut = applySlippage(tokensOut, slippagePct * 100);

    const { txHash } = await executeV4Buy(token, ethWei, minTokensOut, v4State.graduated);

    const tokensPurchasedNum = Number(tokensOut) / 1e18;
    const ethPaidNum = Number(ethWei) / 1e18;

    return {
      txHash,
      trade: {
        side: 'buy',
        amountToken: tokensPurchasedNum,
        amountEth: ethPaidNum,
        priceEth: tokensPurchasedNum > 0 ? ethPaidNum / tokensPurchasedNum : 0,
      },
    };
  }

  const curveState = await fetchBondingCurveState(token, ethPriceUsd);

  // 1. Pre-Graduation: Route to Bonding Curve
  if (!curveState.graduated) {
    if (!curveState.curveAddress) {
      throw new Error(
        'Bonding curve has not been activated for this token yet. Please wait for the token creator to initialize the bonding curve.'
      );
    }

    const { tokensOut } = calculateTokensOut(ethWei, curveState.realEthReserve, curveState.realTokenReserve);
    const minTokensOut = applySlippage(tokensOut, slippagePct * 100);

    const { txHash } = await executeBondingCurveBuy(
      curveState.curveAddress,
      ethWei,
      minTokensOut,
      traderAddr
    );

    const tokensPurchasedNum = Number(tokensOut) / 1e18;
    const ethPaidNum = Number(ethWei) / 1e18;

    return {
      txHash,
      trade: {
        side: 'buy',
        amountToken: tokensPurchasedNum,
        amountEth: ethPaidNum,
        priceEth: tokensPurchasedNum > 0 ? ethPaidNum / tokensPurchasedNum : 0,
      },
    };
  }

  // 2. Post-Graduation: Route to Uniswap V3 via Router
  const { poolAddress, sqrtPriceX96, isTokenFirst } = await getPoolQuote(token);
  const wethIsToken0 = !isTokenFirst;

  const feeWei = (ethWei * BigInt(PROTOCOL_FEE_BPS)) / 10_000n;
  const swapEthWei = ethWei - feeWei;

  const Q192 = 1n << 192n;
  const expectedOut = wethIsToken0
    ? (swapEthWei * sqrtPriceX96 * sqrtPriceX96) / Q192
    : (swapEthWei * Q192) / (sqrtPriceX96 * sqrtPriceX96);

  const amountOutMinimum = applySlippage(expectedOut, slippagePct * 100);
  const deadline = BigInt(Math.floor(Date.now() / 1000) + 1200);

  const buyData = encodeFunctionData({
    abi: INCENTIFI_ROUTER_ABI,
    functionName: 'buyToken',
    args: [token, amountOutMinimum, deadline],
  });

  const txHash = (await provider.request({
    method: 'eth_sendTransaction',
    params: [
      {
        from: traderAddr,
        to: INCENTIFI_SWAP_ROUTER,
        value: toQuantityHex(ethWei),
        data: buyData,
      },
    ],
  })) as string;

  await waitForReceipt(txHash);

  const tokensOutNum = Number(expectedOut) / 1e18;
  const ethPaidNum = Number(ethWei) / 1e18;

  return {
    txHash,
    trade: {
      side: 'buy',
      amountToken: tokensOutNum,
      amountEth: ethPaidNum,
      priceEth: tokensOutNum > 0 ? ethPaidNum / tokensOutNum : 0,
    },
  };
};

/**
 * Universal Sell Function: routes to Bonding Curve pre-graduation, or Uniswap V3 post-graduation.
 */
export const sellToken = async (
  tokenAddress: string,
  trader: string,
  tokenAmount: string | number | bigint,
  slippagePct: number,
  ethPriceUsd: number = REFERENCE_ETH_USD
): Promise<SwapResult> => {
  const provider = getEvmProvider();
  if (!provider) throw new Error('Connect wallet first.');
  await ensureEvmChain();

  const token = getAddress(tokenAddress);
  const traderAddr = getAddress(trader);

  const tokenWei = typeof tokenAmount === 'bigint'
    ? tokenAmount
    : typeof tokenAmount === 'number'
      ? parseUnits(tokenAmount.toFixed(18), 18)
      : parseUnits(String(tokenAmount).trim(), 18);

  if (tokenWei <= 0n) throw new Error('Enter a valid token amount.');

  const venue = await resolveTokenVenue(token);

  // 0a. Legible pool: V4 Quoter + UniversalRouter (Permit2 on the token side), any phase.
  if (venue === 'legible') {
    const { amount: quotedEthOut } = await quoteLegibleSell(token, tokenWei);
    if (quotedEthOut <= 0n) throw new Error('The pool returned a zero quote for this amount.');
    const minEthOut = applySlippage(quotedEthOut, slippagePct * 100);
    const res = await executeLegibleSell(token, tokenWei, minEthOut, quotedEthOut);
    const tokensSoldNum = Number(res.tokenAmount) / 1e18;
    const ethReceivedNum = Number(res.ethAmount) / 1e18;
    return {
      txHash: res.txHash,
      trade: {
        side: 'sell',
        amountToken: tokensSoldNum,
        amountEth: ethReceivedNum,
        priceEth: tokensSoldNum > 0 ? ethReceivedNum / tokensSoldNum : 0,
      },
    };
  }

  // 0b. Previous V4 trio (unchanged).
  if (venue === 'v4-generic' || (venue === 'unknown' && (await isV4LaunchedToken(token)))) {
    const v4State = await fetchV4CurveState(token, ethPriceUsd);
    if (v4State.graduated) {
      throw new Error(
        "This token has graduated to a real, permissionless V4 pool. Trading it directly through this site isn't " +
        'wired up yet — post-graduation V4 trading needs its own dedicated router, which hasn\'t been deployed.'
      );
    }

    const { netEthOut } = calculateEthOut(tokenWei, v4State.realEthReserve, v4State.realTokenReserve);
    const minEthOut = applySlippage(netEthOut, slippagePct * 100);

    const { txHash } = await executeV4Sell(token, tokenWei, minEthOut, v4State.graduated);

    const tokensSoldNum = Number(tokenWei) / 1e18;
    const ethReceivedNum = Number(netEthOut) / 1e18;

    return {
      txHash,
      trade: {
        side: 'sell',
        amountToken: tokensSoldNum,
        amountEth: ethReceivedNum,
        priceEth: tokensSoldNum > 0 ? ethReceivedNum / tokensSoldNum : 0,
      },
    };
  }

  const curveState = await fetchBondingCurveState(token, ethPriceUsd);

  // 1. Pre-Graduation: Route to Bonding Curve
  if (!curveState.graduated) {
    if (!curveState.curveAddress) {
      throw new Error(
        'Bonding curve has not been activated for this token yet. Please wait for the token creator to initialize the bonding curve.'
      );
    }

    const { netEthOut } = calculateEthOut(tokenWei, curveState.realEthReserve, curveState.realTokenReserve);
    const minEthOut = applySlippage(netEthOut, slippagePct * 100);

    const { txHash } = await executeBondingCurveSell(
      curveState.curveAddress,
      token,
      tokenWei,
      minEthOut,
      traderAddr
    );

    const tokensSoldNum = Number(tokenWei) / 1e18;
    const ethReceivedNum = Number(netEthOut) / 1e18;

    return {
      txHash,
      trade: {
        side: 'sell',
        amountToken: tokensSoldNum,
        amountEth: ethReceivedNum,
        priceEth: tokensSoldNum > 0 ? ethReceivedNum / tokensSoldNum : 0,
      },
    };
  }

  // 2. Post-Graduation: Route to Uniswap V3 via Router
  const allowanceData = encodeFunctionData({
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: [traderAddr, INCENTIFI_SWAP_ROUTER],
  });
  const allowanceHex = await provider.request({
    method: 'eth_call',
    params: [{ to: token, data: allowanceData }, 'latest'],
  });
  const currentAllowance = BigInt(allowanceHex || '0x0');

  if (currentAllowance < tokenWei) {
    const approveData = encodeFunctionData({
      abi: ERC20_ABI,
      functionName: 'approve',
      args: [INCENTIFI_SWAP_ROUTER, 2n ** 256n - 1n],
    });
    const approveTx = (await provider.request({
      method: 'eth_sendTransaction',
      params: [{ from: traderAddr, to: token, data: approveData }],
    })) as string;
    await waitForReceipt(approveTx);
  }

  const { poolAddress, sqrtPriceX96, isTokenFirst } = await getPoolQuote(token);
  const tokenIsToken0 = isTokenFirst;

  const Q192 = 1n << 192n;
  const grossEthOut = tokenIsToken0
    ? (tokenWei * sqrtPriceX96 * sqrtPriceX96) / Q192
    : (tokenWei * Q192) / (sqrtPriceX96 * sqrtPriceX96);

  const feeWei = (grossEthOut * BigInt(PROTOCOL_FEE_BPS)) / 10_000n;
  const expectedNetEth = grossEthOut - feeWei;
  const minEthOut = applySlippage(expectedNetEth, slippagePct * 100);
  const deadline = BigInt(Math.floor(Date.now() / 1000) + 1200);

  const sellData = encodeFunctionData({
    abi: INCENTIFI_ROUTER_ABI,
    functionName: 'sellToken',
    args: [token, tokenWei, minEthOut, deadline],
  });

  const txHash = (await provider.request({
    method: 'eth_sendTransaction',
    params: [{ from: traderAddr, to: INCENTIFI_SWAP_ROUTER, data: sellData }],
  })) as string;

  await waitForReceipt(txHash);

  const tokensSoldNum = Number(tokenWei) / 1e18;
  const ethReceivedNum = Number(expectedNetEth) / 1e18;

  return {
    txHash,
    trade: {
      side: 'sell',
      amountToken: tokensSoldNum,
      amountEth: ethReceivedNum,
      priceEth: tokensSoldNum > 0 ? ethReceivedNum / tokensSoldNum : 0,
    },
  };
};
